
import { useEffect, useState } from "react";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";

export default function LegalComplianceView() {
  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const sessionRole = Number(authUser?.role);

  const [legalRecords, setLegalRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= ROLE → API ROLE ================= */
  const getApiRole = () => {
    if (sessionRole === 1) return 2; // Admin → Company
    if (sessionRole === 2) return 5; // Company → Retailer (no UI tabs yet)
    if (sessionRole === 4) return 5; // Agent → Retailer
    return null;
  };

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const role = getApiRole();
    if (!role) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}common/get-users?role=${role}`
        );
        const json = await res.json();

        if (json.status && Array.isArray(json.data)) {
          setLegalRecords(json.data);
        } else {
          setLegalRecords([]);
        }
      } catch (err) {
        console.error("Legal compliance API error", err);
        setLegalRecords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="space-y-4">
      <SectionCard title="Legal & KYC Status">
        {loading ? (
          <div className="py-10 flex justify-center">
            <RegularLoader />
          </div>
        ) : legalRecords.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            No records found
          </div>
        ) : (
          <>
            {/* ================= DESKTOP TABLE ================= */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500">
                    <th className="py-2 text-left">Entity</th>
                    <th className="py-2 text-left">KYC</th>
                    <th className="py-2 text-left">Agreements</th>
                    <th className="py-2 text-left">Payment Terms</th>
                    <th className="py-2 text-left">Last Updated</th>
                  </tr>
                </thead>

                <tbody>
                  {legalRecords.map((l) => (
                    <tr
                      key={l.id}
                      className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
                    >
                      <td className="py-2 text-slate-700">
                        <div className="font-semibold">{l.business_name}</div>
                        <div className="text-[10px] text-slate-500">
                          {l.role === 2
                            ? "Company"
                            : l.role === 4
                            ? "Agent"
                            : "Retailer"}
                        </div>
                      </td>

                      <td className="py-2">
                        Bank: {l.bank_json ? "Verified" : "Pending"}
                        <br />
                        PAN: {l.pan_verified ? "Verified" : "Pending"}
                      </td>

                      <td className="py-2">
                        Agreement: {l.esign_verified ? "Signed" : "Pending"}
                        <br />
                        Privacy: {l.is_mail_verified ? "Yes" : "No"}
                      </td>

                      <td className="py-2">
                        {l.is_payment_success ? "Done" : "Pending"}
                      </td>

                      <td className="py-2">
                        {new Date(l.updated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ================= MOBILE CARDS ================= */}
            <div className="md:hidden space-y-4">
              {legalRecords.map((l) => {
                const paymentDone = l.is_payment_success;

                return (
                  <div
                    key={l.id}
                    className="bg-white rounded-2xl shadow-md border border-slate-400 p-5 transition hover:shadow-md"
                  >
                    {/* HEADER */}
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-800 leading-tight">
                          {l.business_name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {l.role === 2
                            ? "Company"
                            : l.role === 4
                            ? "Agent"
                            : "Retailer"}
                        </p>
                      </div>

                      <span
                        className={`text-[11px] px-3 py-1 rounded-full font-medium
                          ${
                            paymentDone
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {paymentDone ? "Payment Done" : "Payment Pending"}
                      </span>
                    </div>

                    {/* DIVIDER */}
                    <div className="my-4 border-t border-slate-100" />

                    {/* DETAILS GRID */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-xs">
                      <div>
                        <p className="text-slate-400">Bank</p>
                        <p
                          className={`font-medium ${
                            l.bank_json ? "text-green-600" : "text-amber-600"
                          }`}
                        >
                          {l.bank_json ? "Verified" : "Pending"}
                        </p>
                      </div>

                      <div>
                        <p className="text-slate-400">PAN</p>
                        <p
                          className={`font-medium ${
                            l.pan_verified ? "text-green-600" : "text-amber-600"
                          }`}
                        >
                          {l.pan_verified ? "Verified" : "Pending"}
                        </p>
                      </div>

                      <div>
                        <p className="text-slate-400">Agreement</p>
                        <p
                          className={`font-medium ${
                            l.esign_verified ? "text-green-600" : "text-amber-600"
                          }`}
                        >
                          {l.esign_verified ? "Signed" : "Pending"}
                        </p>
                      </div>

                      <div>
                        <p className="text-slate-400">Privacy Consent</p>
                        <p
                          className={`font-medium ${
                            l.is_mail_verified ? "text-green-600" : "text-amber-600"
                          }`}
                        >
                          {l.is_mail_verified ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>

                    {/* FOOTER */}
                    <div className="mt-5 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                      Updated: {new Date(l.updated_at).toLocaleDateString("en-GB")}
                    </div>
                  </div>
                );
              })}
            </div>

          </>
        )}
      </SectionCard>


      <SectionCard title="How Legal & Documents are sourced">
        <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
          <li>
            Retailer portal collects Aadhaar & PAN via DigiLocker, and shows
            submitted documents & agreement history.
          </li>
          <li>
            Customer portal shows privacy policy, terms & conditions, consent logs
            related to warranty purchase and claim.
          </li>
          <li>
            Admin can see a single-view of which retailers/customers have accepted
            which legal documents & payment terms.
          </li>
        </ul>
      </SectionCard>
    </div>
  );
}





// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";

// const legalRecords = [
//   {
//     id: 1,
//     entityType: "Retailer",
//     entityName: "Shree Mobile Store",
//     aadhaar: "Verified via DigiLocker",
//     pan: "Verified",
//     agreementStatus: "Signed",
//     privacyAccepted: true,
//     paymentTerms: "Monthly, 15 days credit",
//     lastUpdated: "2025-09-01",
//   },
//   {
//     id: 2,
//     entityType: "Retailer",
//     entityName: "A1 Electronics",
//     aadhaar: "Pending",
//     pan: "Verified",
//     agreementStatus: "Partially Signed",
//     privacyAccepted: false,
//     paymentTerms: "Weekly, advance",
//     lastUpdated: "2025-10-10",
//   },
// ];

// export default function LegalComplianceView() {
//   return (
//     <div className="space-y-4">
//       <SectionCard title="Legal & KYC Status (Retailers)">
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Entity</th>
//                 <th className="py-2 text-left">KYC</th>
//                 <th className="py-2 text-left">Agreements</th>
//                 <th className="py-2 text-left">Payment Terms</th>
//                 <th className="py-2 text-left">Last Updated</th>
//               </tr>
//             </thead>
//             <tbody>
//               {legalRecords.map((l) => (
//                 <tr
//                   key={l.id}
//                   className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                 >
//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div className="font-semibold">{l.entityName}</div>
//                     <div className="text-[10px] text-slate-500">{l.entityType}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Aadhaar: {l.aadhaar}</div>
//                     <div>PAN: {l.pan}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Agreement: {l.agreementStatus}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Privacy consent: {l.privacyAccepted ? "Yes" : "No"}
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">{l.paymentTerms}</td>
//                   <td className="py-1.5 text-[11px] text-slate-700">{l.lastUpdated}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="How Legal & Documents are sourced">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             Retailer portal collects Aadhaar & PAN via DigiLocker, and shows
//             submitted documents & agreement history.
//           </li>
//           <li>
//             Customer portal shows privacy policy, terms & conditions, consent logs
//             related to warranty purchase and claim.
//           </li>
//           <li>
//             Admin can see a single-view of which retailers/customers have accepted
//             which legal documents & payment terms.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }

// -----------------------------------------------------------------------------
// Retailer Users – user access under each retailer with activate / deactivate
// -----------------------------------------------------------------------------